//@flow
import * as Immutable from 'immutable';
import EventGroupActions from 'common/redux/actions/eventGroup';
import { mapFromList, mergeDeepWithRemoval } from 'common/utils/ImmutableUtils';

const initialState = Immutable.fromJS({ records: {} });

const convertEventGroupToMap = eventGroup => {
    return (
        eventGroup &&
        eventGroup.reduce((lookup, item) => {
            return lookup.set(
                item.get('definitionId'),
                item.set('events', mapFromList(item.get('events'), 'eventId'))
            );
        }, Immutable.Map())
    );
};

const mergeEventGroups = (previousEventGroups, nextEventGroups) => {
    if (!previousEventGroups) {
        return nextEventGroups;
    }
    const previousEventGroupMap = convertEventGroupToMap(previousEventGroups);
    const nextEventGroupMap = convertEventGroupToMap(nextEventGroups);

    let mergedEventGroups = Immutable.List();
    nextEventGroupMap.keySeq().forEach(key => {
        const newEvent = nextEventGroupMap.get(key);
        const oldEvent = previousEventGroupMap.get(key);
        const mergedEvents = mergeDeepWithRemoval(
            oldEvent ? oldEvent.get('events') : Immutable.OrderedMap(),
            newEvent.get('events')
        );
        mergedEventGroups = mergedEventGroups.push(
            newEvent.merge(Immutable.Map({ events: mergedEvents.valueSeq() }))
        );
    });
    return mergedEventGroups;
};

const setEventGroupProgress = ({
    storedEventGroup,
    eventIds,
    multiple,
    inProgress = true,
    params
}) => {
    if (params.definitionId === storedEventGroup.get('definitionId')) {
        return storedEventGroup.withMutations(eventGroup => {
            if (multiple) {
                eventGroup.set('isResolveInProgress', inProgress);
            }
            const newEvents = eventGroup.get('events').map(event => {
                if (eventIds.includes(event.get('eventId'))) {
                    return event.set('isResolveInProgress', inProgress);
                }
                return event;
            });
            return eventGroup.set('events', newEvents);
        });
    }
    return storedEventGroup;
};
export const eventGroup = (
    state: Object = initialState,
    action: Object
): Object => {
    switch (action.type) {
        case EventGroupActions.FETCH_EVENTGROUPS_SUCCESS: {
            const nextEventGroups = Immutable.fromJS(action.response);
            const { params } = action;
            const previousEventGroups = state.getIn([
                'records',
                params.clientId,
                params.date,
                params.carrierTeam,
                params.userId,
                'entity',
                'eventGroups'
            ]);

            const mergedEventGroups = mergeEventGroups(
                previousEventGroups,
                nextEventGroups
            );

            return state.withMutations(mutableState => {
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'entity',
                        'eventGroups'
                    ],
                    mergedEventGroups
                );
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'status',
                        'isLoading'
                    ],
                    false
                );
            });
        }

        case EventGroupActions.FETCH_EVENTCOUNT_START:
        case EventGroupActions.FETCH_EVENTGROUPS_START: {
            const { params } = action;
            return state.setIn(
                [
                    'records',
                    params.clientId,
                    params.date,
                    params.carrierTeam,
                    params.userId,
                    'status',
                    'isLoading'
                ],
                true
            );
        }

        case EventGroupActions.FETCH_EVENTGROUPS_ERROR: {
            const { params, response } = action;
            return state.withMutations(mutableState => {
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'status',
                        'error'
                    ],
                    response
                );
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'status',
                        'isLoading'
                    ],
                    false
                );
            });
        }

        case EventGroupActions.FETCH_EVENTGROUP_SUCCESS: {
            var newEventGroups;
            var eventGroup = Immutable.fromJS(action.response);
            const { params } = action;
            var eventGroups = state.getIn([
                'records',
                params.clientId,
                params.date,
                params.carrierTeam,
                params.userId,
                'entity',
                'eventGroups'
            ]);
            if (eventGroup.get('events').size === 0) {
                newEventGroups = eventGroups.filter(storedEventGroup => {
                    return (
                        eventGroup.get('definitionId') !==
                        storedEventGroup.get('definitionId')
                    );
                });
            } else {
                newEventGroups = eventGroups.map(storedEventGroup => {
                    if (
                        eventGroup.get('definitionId') ===
                        storedEventGroup.get('definitionId')
                    ) {
                        return eventGroup;
                    }
                    return storedEventGroup;
                });
            }

            return state.withMutations(mutableState => {
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'entity',
                        'eventGroups'
                    ],
                    newEventGroups
                );

                const count = newEventGroups.reduce((acc, curr) => {
                    return acc + curr.getIn(['events']).size;
                }, 0);

                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'entity',
                        'eventsCount'
                    ],
                    count
                );
            });
        }

        case EventGroupActions.RESOLVE_EVENTGROUP_START: {
            const { params, eventIds, multiple } = action;
            const eventGroupEntities = state.getIn([
                'records',
                params.clientId,
                params.date,
                params.carrierTeam,
                params.userId,
                'entity',
                'eventGroups'
            ]);
            const newEventGroups = eventGroupEntities.map(storedEventGroup => {
                return setEventGroupProgress({
                    params,
                    storedEventGroup,
                    eventIds,
                    multiple,
                    inProgress: true
                });
            });
            return state.setIn(
                [
                    'records',
                    params.clientId,
                    params.date,
                    params.carrierTeam,
                    params.userId,
                    'entity',
                    'eventGroups'
                ],
                newEventGroups
            );
        }
        case EventGroupActions.RESOLVE_EVENTGROUP_SUCCESS: {
            const { params, eventIds, multiple } = action;
            const eventGroupEntities = state.getIn([
                'records',
                params.clientId,
                params.date,
                params.carrierTeam,
                params.userId,
                'entity',
                'eventGroups'
            ]);
            const newEventGroups = eventGroupEntities.map(storedEventGroup => {
                return setEventGroupProgress({
                    params,
                    storedEventGroup,
                    eventIds,
                    multiple,
                    inProgress: false
                });
            });
            return state.setIn(
                [
                    'records',
                    params.clientId,
                    params.date,
                    params.carrierTeam,
                    params.userId,
                    'entity',
                    'eventGroups'
                ],
                newEventGroups
            );
        }
        case EventGroupActions.RESOLVE_EVENTGROUP_ERROR: {
            const { params, eventIds, multiple } = action;
            const eventGroupEntities = state.getIn([
                'records',
                params.clientId,
                params.date,
                params.carrierTeam,
                params.userId,
                'entity',
                'eventGroups'
            ]);
            const newEventGroups = eventGroupEntities.map(storedEventGroup => {
                return setEventGroupProgress({
                    params,
                    storedEventGroup,
                    eventIds,
                    multiple,
                    inProgress: false
                });
            });
            return state.setIn(
                [
                    'records',
                    params.clientId,
                    params.date,
                    params.carrierTeam,
                    params.userId,
                    'entity',
                    'eventGroups'
                ],
                newEventGroups
            );
        }

        case EventGroupActions.FETCH_EVENTCOUNT_SUCCESS: {
            const { params, response } = action;

            return state.withMutations(mutableState => {
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'entity',
                        'eventsCount'
                    ],
                    response
                );
                mutableState.setIn(
                    [
                        'records',
                        params.clientId,
                        params.date,
                        params.carrierTeam,
                        params.userId,
                        'status',
                        'isLoading'
                    ],
                    false
                );
            });
        }

        default:
            return state;
    }
};
